package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initKorePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("kore.md", FrontMatterElement.Builder { addScalar("title", "kore"); addScalar("searchTitle", "Ordered shutdown, health probes and typed config for Kotlin servers — KMP, native-first"); addScalar("description", "The process lifecycle of a Kotlin server binary, owned once: shutdown in a defined order, three health questions, a typed configuration that refuses an unknown variable, and /version with the commit."); addScalar("library", "kore"); }))
}

@Page("/kore")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun KorePage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every Kotlin service ends up writing the same four things, slightly differently, and getting one of them subtly wrong: the shutdown, the health endpoints, the environment reading, and the build stamp. Each is small enough to write in an afternoon and none of them is small enough to get right by accident — and the one that fails does so invisibly, because a shutdown is the thing nobody watches. kore is those four, once, for a Ktor service, and it is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("native-first")
        }
        org.jetbrains.compose.web.dom.Text(", because the facts that make it necessary are invisible from the JVM.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The sharpest one: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Ktor's ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("EmbeddedServer.stop")
            }
            org.jetbrains.compose.web.dom.Text(" runs its steps in the opposite order on the JVM and on Kotlin/Native.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ApplicationStopping")
        }
        org.jetbrains.compose.web.dom.Text(" — where every example tells you to close your pool and your broker connection — runs ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("after")
        }
        org.jetbrains.compose.web.dom.Text(" the drain on one platform and ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("before")
        }
        org.jetbrains.compose.web.dom.Text(" it on the other, from identical source, with nothing in the API saying so. A pool closed before the drain is a request that was already being served and now fails.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("A shutdown whose order is the specification")
                }
                org.jetbrains.compose.web.dom.Text(", not a side effect:")
            }
            org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""SIGTERM
  → announce   readiness goes false, then a wait long enough to matter
  → drain      accept stops; in-flight finishes; new arrivals get 503 + Connection: close
  → release    consumers flush then close, then pools, then telemetry — each with its own deadline
  → exit       inside the grace period, on its own
""") } }
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.Text("Asserted twice: a property test over the stage machine, and an end-to-end run that sends a real ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("SIGTERM")
                }
                org.jetbrains.compose.web.dom.Text(" to a real container under load. Both were written ")
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("before")
                }
                org.jetbrains.compose.web.dom.Text(" the implementation.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("Three probes that ask different questions.")
                }
                org.jetbrains.compose.web.dom.Text(" ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("startup")
                }
                org.jetbrains.compose.web.dom.Text(", ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("ready")
                }
                org.jetbrains.compose.web.dom.Text(" and ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("live")
                }
                org.jetbrains.compose.web.dom.Text(", with dependency checks — not one ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("/health")
                }
                org.jetbrains.compose.web.dom.Text(" wired to all three, which is what a chart does when nobody stops it. A check that has never run reports ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("503")
                }
                org.jetbrains.compose.web.dom.Text(", and so does a cached one past its refresh budget: a stale result is not a healthy result.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("A typed configuration from the environment")
                }
                org.jetbrains.compose.web.dom.Text(" with ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("--print-config")
                }
                org.jetbrains.compose.web.dom.Text(" and a refusal on an unknown variable under the prefix — including a near miss, where the message names the declared variable you probably meant. Half the \"it is different in prod\" incidents are a variable nothing reads.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/version")
                    }
                    org.jetbrains.compose.web.dom.Text(" with the commit and the build date")
                }
                org.jetbrains.compose.web.dom.Text(", compiled in by a Gradle plugin rather than read at runtime, so a deployment cannot get it wrong the way it got the image tag wrong.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("tracy, metrik and katcher wired in one call")
                }
                org.jetbrains.compose.web.dom.Text(", flushed in the right order on the way out.")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Explicitly not: a DI container, a router, a configuration framework with seven sources, or an observability library.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-costs") }) {
        org.jetbrains.compose.web.dom.Text("What it costs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Measured, not estimated — fifteen alternating runs per cell, the same binary with kore and without:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("time to first ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/health")
                    }
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("RSS at ready")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("stop under load")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("JVM")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Em {
                        org.jetbrains.compose.web.dom.Text("noise")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("+5.6 MB")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("+2.0 s")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("native")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Em {
                        org.jetbrains.compose.web.dom.Text("noise")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("+1.6 MB")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("+2.0 s")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The stop column is not a cost.")
        }
        org.jetbrains.compose.web.dom.Text(" In those same runs the arm without kore finished ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("none")
        }
        org.jetbrains.compose.web.dom.Text(" of the requests in flight when the signal arrived and dropped all of them — both platforms, every round — while the arm with kore finished all of them. Two seconds is what the work costs when it is not thrown away.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The startup column is printed as noise because three runs of the same harness gave +12 ms, −23 ms and +30 ms. An earlier version of that table called it \"+1 %\", which read as a measured cost and was an artefact of which afternoon the run happened on.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories {
    maven("https://reposilite.kotlin.website/snapshots") {
        content { includeGroupByRegex("io\\.github\\.youndie.*") }
    }
}

dependencies {
    implementation("io.github.youndie:kore-core:0.1.3")
    implementation("io.github.youndie:kore-ktor:0.1.3")
}

plugins {
    id("io.github.youndie.kore.build") version "0.1.3"   // what /version reports
}
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-it-actually-is") }) {
        org.jetbrains.compose.web.dom.Text("Where it actually is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("All five features built, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("0.1.3")
        }
        org.jetbrains.compose.web.dom.Text(" published, 51 backlog items closed, suites running on arm64 Linux and Apple silicon on every pull request. Nothing is on Maven Central yet.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first real adoption found four defects in a day that none of that caught — among them a documented example that cannot run on the JVM, and a module that had never been published at all. That is the honest measure of where this is: the library works, and the things a consumer trips over are still being found by consumers.")
    }
}
